import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Sparkles, Copy, CheckCircle2, Loader2, AlertTriangle, Mail, LifeBuoy, Gift } from "lucide-react";
import { toast } from "sonner";
import { checkoutStatus } from "../lib/api";
import { storePro } from "../lib/pro";

const MAX_POLLS = 20; // ~40s at 2s interval
const INTERVAL_MS = 2000;

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [phase, setPhase] = useState("polling"); // polling | paid | expired | failed | timeout
  const [result, setResult] = useState(null);
  const pollsRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let timer;
    // Deferred-via-setTimeout(0) so the effect body contains no synchronous
    // setState calls — satisfies react-hooks/set-state-in-effect.
    const start = setTimeout(() => {
      if (!sessionId) {
        setPhase("failed");
        return;
      }

      const tick = async () => {
        if (cancelled) return;
        pollsRef.current += 1;
        try {
          const data = await checkoutStatus(sessionId);
          if (cancelled) return;
          // Gift purchases: no license_key returned (it goes to the recipient).
          if (data.payment_status === "paid" && data.gift) {
            setResult(data);
            setPhase("paid");
            return;
          }
          if (data.payment_status === "paid" && data.license_key) {
            setResult(data);
            setPhase("paid");
            storePro({ email: data.email, license_key: data.license_key });
            return;
          }
          if (data.status === "expired") {
            setPhase("expired");
            return;
          }
          if (pollsRef.current >= MAX_POLLS) {
            setPhase("timeout");
            return;
          }
          timer = setTimeout(tick, INTERVAL_MS);
        } catch (err) {
          if (cancelled) return;
          if (pollsRef.current >= MAX_POLLS) {
            setPhase("failed");
            return;
          }
          timer = setTimeout(tick, INTERVAL_MS);
        }
      };

      tick();
    }, 0);

    return () => {
      cancelled = true;
      if (start) clearTimeout(start);
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(result.license_key);
      toast.success("License key copied");
    } catch {
      toast.error("Could not copy — long-press to copy manually");
    }
  };

  const copyRescueLink = async () => {
    try {
      const url = `${window.location.origin}/pro?resend=${encodeURIComponent(result.email)}`;
      await navigator.clipboard.writeText(url);
      toast.success("Rescue link copied — keep it somewhere safe");
    } catch {
      toast.error("Could not copy — long-press to copy manually");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
      {phase === "polling" && (
        <div data-testid="success-polling" className="text-center">
          <Loader2 className="mx-auto animate-spin" size={32} />
          <h1 className="mt-6 text-3xl font-black tracking-tight">
            Confirming payment…
          </h1>
          <p className="mt-3 text-muted-foreground">
            Stripe is settling your transaction. This usually takes a few seconds.
          </p>
        </div>
      )}

      {phase === "paid" && result && result.gift && (
        <div data-testid="success-gift" className="border border-border bg-card p-8 lg:p-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 grid place-items-center bg-primary text-primary-foreground rounded-sm">
              <Gift size={18} />
            </div>
            <div>
              <div className="overline">Payment confirmed</div>
              <h1 className="text-3xl font-black tracking-tight">Your gift is on its way</h1>
            </div>
          </div>
          <p className="mt-5 text-muted-foreground">
            Thanks for sharing the lab. We&rsquo;ve just emailed an Aviary Lab Pro
            license to{" "}
            <span className="font-mono font-semibold text-foreground">
              {result.gift_recipient_email}
            </span>{" "}
            with your personal note. They&rsquo;ll be unlocking the calculator
            within minutes.
          </p>
          <div className="mt-5 border border-border p-4 text-xs space-y-1">
            <div className="flex items-start gap-2">
              <Mail size={14} className="mt-0.5 shrink-0" />
              <span>
                A confirmation receipt has been sent to your email too. If the
                recipient doesn&rsquo;t see the gift, ask them to check Promotions
                / Spam.
              </span>
            </div>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/pro"
              data-testid="success-gift-another"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 rounded-sm hover:bg-[hsl(222,89%,42%)] transition-colors font-medium"
            >
              <Gift size={16} /> Send another gift
            </Link>
            <Link
              to="/encyclopedia"
              className="inline-flex items-center gap-2 border border-border px-5 py-3 rounded-sm hover:bg-secondary transition-colors font-medium"
            >
              Back to Index
            </Link>
          </div>
        </div>
      )}

      {phase === "paid" && result && !result.gift && (
        <div data-testid="success-paid" className="border border-border bg-card p-8 lg:p-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 grid place-items-center bg-primary text-primary-foreground rounded-sm">
              <CheckCircle2 size={18} />
            </div>
            <div>
              <div className="overline">Payment confirmed</div>
              <h1 className="text-3xl font-black tracking-tight">Welcome to Pro</h1>
            </div>
          </div>

          <p className="mt-5 text-muted-foreground">
            Your license has been generated and saved on this device. Keep this
            key safe — you&rsquo;ll need it to unlock the lab on other browsers.
          </p>

          <div className="mt-6">
            <div className="overline mb-1">Your license key</div>
            <div className="flex items-stretch gap-2">
              <div
                data-testid="success-license-key"
                className="flex-1 font-mono tracking-wider text-base bg-foreground text-background px-4 py-3 select-all"
              >
                {result.license_key}
              </div>
              <button
                type="button"
                onClick={copyKey}
                data-testid="success-copy-key"
                className="border border-border px-3 hover:bg-secondary transition-colors"
                aria-label="Copy license"
              >
                <Copy size={16} />
              </button>
            </div>
            <div className="mt-2 text-xs font-mono text-muted-foreground">
              tied to · {result.email}
            </div>
          </div>

          <div className="mt-5 flex items-start gap-2 border border-border p-3 text-xs">
            <Mail size={14} className="mt-0.5 shrink-0" />
            <span>
              {result.email_sent
                ? `A copy has been emailed to ${result.email}.`
                : "Email delivery isn't configured on this instance — please copy the key above and store it safely."}
            </span>
          </div>

          <button
            type="button"
            onClick={copyRescueLink}
            data-testid="success-copy-rescue-link"
            className="mt-3 w-full inline-flex items-center justify-between gap-3 border border-dashed border-border bg-secondary/40 px-3 py-2.5 hover:bg-secondary transition-colors text-xs text-left"
          >
            <span className="flex items-center gap-2">
              <LifeBuoy size={14} className="shrink-0" />
              <span>
                <span className="font-medium text-foreground">Save your rescue link.</span>
                <span className="text-muted-foreground"> One click re-sends your key — bookmark it or forward the receipt.</span>
              </span>
            </span>
            <Copy size={12} className="shrink-0 text-muted-foreground" />
          </button>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => navigate("/calculator")}
              data-testid="success-open-calculator"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 rounded-sm hover:bg-[hsl(222,89%,42%)] transition-colors font-medium"
            >
              <Sparkles size={16} /> Open the Calculator
            </button>
            <Link
              to="/encyclopedia"
              className="inline-flex items-center gap-2 border border-border px-5 py-3 rounded-sm hover:bg-secondary transition-colors font-medium"
            >
              Back to Index
            </Link>
          </div>
        </div>
      )}

      {(phase === "expired" || phase === "failed" || phase === "timeout") && (
        <div data-testid="success-error" className="border border-border bg-card p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 grid place-items-center bg-destructive text-destructive-foreground rounded-sm">
              <AlertTriangle size={18} />
            </div>
            <h1 className="text-2xl font-black tracking-tight">
              {phase === "expired" ? "Checkout expired" : "We couldn't confirm payment"}
            </h1>
          </div>
          <p className="text-muted-foreground">
            {phase === "expired"
              ? "Your Stripe session expired before completing. No charge was made."
              : "We waited but didn't see a confirmed payment. If you were charged, contact support with your purchase email and we'll resend your license."}
          </p>
          <div className="mt-6 flex gap-3">
            <Link
              to="/pro"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-sm hover:bg-[hsl(222,89%,42%)] transition-colors text-sm"
            >
              Try again
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 border border-border px-4 py-2.5 rounded-sm hover:bg-secondary transition-colors text-sm"
            >
              Home
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

